// ============================================================================
// src/dashboard/HomeDashboard.jsx  —  Phase 2 Round 7
// ----------------------------------------------------------------------------
// The home view. Hook-based extraction of the ~540-line inline block that
// used to live inside App() as `if(view==="home") {...}`.
//
// DATA OWNERSHIP, B3.3. This surface now owns six scoped catalog resources —
// equipment maintenance, mig-048 materials, the compact production card,
// `home.standard.alert.inputs` (the six bounded daily/livestock projections
// App's legacy boot sidecar used to publish), `home.standard.planner` (the
// seven persisted `ppp-*` app_store blobs: the broiler batch list, the four pig
// planner values, the layer-group directory and the cleared-missed key set),
// and `home.standard.animal.layerEvidence` (the COMPLETE layer_dailys count
// evidence that used to arrive as LayerContext.allLayerDailys). Taking that
// last one retired App's boot sidecar outright — it was the only one of the
// sidecar's eight publications any component ever read back.
//
// What it still reads from App-owned context is written down, not implied: the
// layer batch/housing directories and the full-history broiler and pig daily
// reports — all four consumed as COMPLETE histories by
// buildAnimalHistorySnapshot.
// `src/app/data/migratedSurfaces/app.home.standard.ts` declares exactly that
// list as `residualLegacy`, which is why this surface is `partial` and not
// boot-release eligible.
// ============================================================================
import React from 'react';
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {
  AnimalHistoryLayerDirectoryData,
  HomeStandardAlertInputsData,
  HomeStandardAnimalDailyHistoryData,
  HomeStandardEquipmentAttentionData,
  HomeStandardEquipmentMaterialsData,
  HomeStandardLayerEvidenceData,
  HomeStandardPlannerData,
  HomeStandardProductionSourcesData,
} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {EquipmentMaterialClearCreateVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import './homeRedesign.css';
import {sb} from '../lib/supabase.js';
import {clearAllMissedOnServer, clearMissedEntryOnServer} from '../lib/appStoreOperationApi.js';
import {fmt, fmtS, toISO, addDays, todayCentralISO} from '../lib/dateUtils.js';
import {calcPoultryStatus} from '../lib/broiler.js';
import {calcBreedingTimeline, buildCycleSeqMap, activePigFeederDailyTargets} from '../lib/pig.js';
import {buildMaterialChecklist} from '../lib/equipmentMaterials.js';
import {buildAnimalHistorySnapshot} from '../lib/animalHistory.js';
import {centralGreeting, msUntilNextGreetingBoundary} from '../lib/centralClock.js';
import {buildEquipmentAttention, buildMissedDailyReports, buildMissingData, buildNext30Events} from './homeAlerts.js';
import {canAccessProgram, canSeeAllDailyReportsFeed, canSeeLivestockHomeSections} from '../lib/access.js';
import type {AuthState} from '../lib/access.js';
import {filterActiveLayerGroups} from '../lib/layerGroupStatus.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import PlannerIcon from '../components/PlannerIcon.jsx';
import {ANIMAL_ICON_KEYS} from '../lib/plannerIcons.js';
import {openableProps} from '../shared/openable.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {
  DailyRecordCard,
  EggSummaryCard,
  feedLbsVal,
  feedListVal,
  gritVal,
  countVal,
  tagVal,
  mutedVal,
  voltageVal,
  check,
  mortText,
  commentText,
} from '../shared/DailyRecordCards.jsx';
import {useAuth} from '../contexts/AuthContext.jsx';
import {canAccessProduction, type AuthState as AccessAuthState} from '../lib/access.js';
import {useUI} from '../contexts/UIContext.jsx';
import {buildProductionModel, homeProductionStats} from '../lib/production.js';
import type {MaterialClearRow, MaterialGroup, MaterialRow} from '../lib/equipmentMaterials.js';
import Header from '../shared/Header.tsx';
import Button from '../shared/Button.js';
import Badge from '../shared/Badge.js';

// Home aggregates rows from several legacy SQL/jsonb tables. Those context
// providers intentionally expose their row arrays as unknown[] because they
// do not own database schemas; this surface is the typed read boundary and
// only accesses the fields it renders through this open row shape.
type ErrorLike = {message?: string};

// The catalog rethrows PostgREST's `error.message` unchanged, so reading
// `.message` off the rejection reproduces the text the `{error}` envelope
// produced. Identical to the helper the other converted surfaces carry.
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);

type HomeRecord = Record<string, any>;
type HomeAuthState = {role?: string; name?: string} | null;
type HomeRouteMap = Record<string, (id: string) => string>;
type HomeComingSoonProps = {Header?: typeof Header; label: string; onBack: () => void};
type HomeChevronProps = {className?: string};

// Daily-report kind → dedicated record-page route. Home "Last 5 Days" tiles
// navigate straight to the record page instead of waking the legacy dailys-hub
// edit modal (the old setPendingEdit + setView path). Egg dailys live under the
// layer program at /layer/eggs.
const DAILY_RECORD_ROUTES: HomeRouteMap = {
  broiler: (id) => '/broiler/dailys/' + id,
  pig: (id) => '/pig/dailys/' + id,
  layer: (id) => '/layer/dailys/' + id,
  egg: (id) => '/layer/eggs/' + id,
  cattle: (id) => '/cattle/dailys/' + id,
  sheep: (id) => '/sheep/dailys/' + id,
};
function pathForDailyReport(r: HomeRecord | null | undefined) {
  const build = r ? DAILY_RECORD_ROUTES[String(r.kind)] : undefined;
  return build && r ? build(r.id) : null;
}

// Herd/flock dot + label for the Home "Last 5 Days" feed — same production
// palette the cattle/sheep list pages use, so a herd's dot is identical on Home
// and on its program page.
const HOME_HERD: {labels: Record<string, string>; dot: Record<string, string>} = {
  labels: {mommas: 'Mommas', backgrounders: 'Backgrounders', finishers: 'Finishers', bulls: 'Bulls'},
  dot: {mommas: '#991b1b', backgrounders: '#9a3412', finishers: '#9f1239', bulls: '#7f1d1d'},
};
const HOME_FLOCK: {labels: Record<string, string>; dot: Record<string, string>} = {
  labels: {rams: 'Rams', ewes: 'Ewes', feeders: 'Feeders'},
  dot: {rams: '#0f766e', ewes: '#86198f', feeders: '#854d0e'},
};
function homeSheepComment(c: unknown) {
  const t = c == null ? '' : String(c).trim();
  const low = t.toLowerCase();
  if (!t || ['none', '0', 'n/a', 'na', '-'].includes(low)) return null;
  return commentText(t);
}

// Map a daily record to the shared DailyRecordCard display model. Mirrors each
// program list view's mapRow so the Home feed and the program pages read the
// same fields the same way.
function buildHomeDailyModel(kind: string, d: HomeRecord): HomeRecord {
  const base = {team: d.team_member || '—', source: d.source, photos: d.photos};
  if (kind === 'broiler') {
    return {
      ...base,
      name: d.batch_label || '—',
      vals: {feed: feedLbsVal(d.feed_lbs), feedTag: d.feed_type ? tagVal(d.feed_type) : '', grit: gritVal(d.grit_lbs)},
      checks: [check('Moved', d.group_moved !== false), check('Waterer', d.waterer_checked !== false)],
      mort: mortText(d.mortality_count, d.mortality_reason),
      comment: commentText(d.comments),
    };
  }
  if (kind === 'layer') {
    return {
      ...base,
      name: d.batch_label || '—',
      vals: {
        feed: feedLbsVal(d.feed_lbs),
        feedTag: d.feed_type ? tagVal(d.feed_type) : '',
        grit: gritVal(d.grit_lbs),
        count: countVal(d.layer_count),
      },
      checks: [check('Moved', d.group_moved !== false), check('Waterer', d.waterer_checked !== false)],
      mort: mortText(d.mortality_count, d.mortality_reason),
      comment: commentText(d.comments),
    };
  }
  if (kind === 'pig') {
    return {
      ...base,
      name: d.batch_label || '—',
      vals: {
        feed: feedLbsVal(d.feed_lbs),
        pigs: parseInt(d.pig_count) > 0 ? d.pig_count + ' pigs' : mutedVal('—'),
        volt: voltageVal(d.fence_voltage),
      },
      checks: [
        check('Moved', d.group_moved !== false),
        check('Nipple', d.nipple_drinker_working !== false),
        check('Fence', d.fence_walked !== false),
      ],
      comment: commentText(d.issues),
    };
  }
  if (kind === 'cattle') {
    return {
      ...base,
      name: HOME_HERD.labels[d.herd] || d.herd || '—',
      dot: HOME_HERD.dot[d.herd] || HOME_HERD.dot.mommas,
      vals: {feed: feedListVal(d.feeds, d.minerals), volt: voltageVal(d.fence_voltage)},
      checks: [check('Water', d.water_checked !== false)],
      mort: mortText(d.mortality_count, d.mortality_reason),
      comment: commentText(d.issues),
    };
  }
  if (kind === 'sheep') {
    return {
      ...base,
      name: HOME_FLOCK.labels[d.flock] || d.flock || '—',
      dot: HOME_FLOCK.dot[d.flock] || HOME_FLOCK.dot.ewes,
      vals: {feed: feedListVal(d.feeds, d.minerals), volt: voltageVal(d.fence_voltage_kv)},
      checks: [check('Water', d.waterers_working !== false)],
      mort: mortText(d.mortality_count, null),
      comment: homeSheepComment(d.comments),
    };
  }
  return {...base, name: d.batch_label || '—', vals: {}, checks: []};
}

// Homepage-redesign inline glyphs (currentColor-driven so the parent class
// controls stroke). Chevron is the lift/tile reveal affordance; Check is the
// filled-circle "all caught up" mark inside .note-ok rows.
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function Chevron({className}: HomeChevronProps) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// Equipment-attention section glyph. Inline SVG (no raster background — the
// planner wrench PNG ships with a baked light-gray box) so it stays flat and
// crisp at any size; color comes from the parent via currentColor.
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function WrenchGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

// Coming-soon destination for not-yet-built top-level areas (Processing and
// future sections). Rendered as a full in-app page via local
// HomeDashboard state — a real, safe destination, NOT a broken route. Keeps
// the app chrome (Header) and offers an explicit Back to Home.
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function ComingSoonPage({label, onBack}: HomeComingSoonProps) {
  return (
    <div className="home theme-crisp home-dashboard">
      <Header />
      <main className="home-col">
        <section className="card" data-coming-soon={label} style={{padding: '44px 28px', textAlign: 'center'}}>
          <div className="section-label" style={{justifyContent: 'center'}}>
            {label}
          </div>
          <div style={{fontSize: 21, fontWeight: 750, letterSpacing: '-0.02em', margin: '10px 0 6px'}}>Coming soon</div>
          <div style={{fontSize: 13.5, color: 'var(--text-muted)', maxWidth: 420, margin: '0 auto 22px'}}>
            This section is being built and will be available in a future update.
          </div>
          <Button variant="secondary" onClick={onBack}>
            ← Back to Home
          </Button>
        </section>
      </main>
    </div>
  );
}

export default function HomeDashboard() {
  const {authState: authStateValue} = useAuth();
  const authState = (authStateValue && typeof authStateValue === 'object' ? authStateValue : null) as HomeAuthState;
  // The seven persisted `ppp-*` planner blobs are this surface's own read now,
  // through home.standard.planner. They used to arrive as BatchesContext,
  // PigContext, LayerContext.layerGroups and FeedCostsContext.missedCleared,
  // all four published by App's `ppp-*` app_store boot transaction — which is
  // not merely a read: it REPAIRS and PERSISTS ppp-v4 and ppp-feeders-v1. The
  // resource reproduces the display half of those repairs as an immutable
  // projection and writes nothing, exactly as home.light.planner does for the
  // Light portal over the identical seven keys.
  //
  // Do NOT reintroduce useBatches / usePig / useFeedCosts here, and do not take
  // layerGroups back off useLayer: a provider-hook field read is precisely what
  // tests/static/former_eager_evidence_static.test.js scores as consumption,
  // and `home`'s retirement from the frozen app_store consumer list in
  // tests/fixtures/former-eager-dependencies.json would become a false claim.
  const plannerResource = useRouteResource(RESOURCE.HOME_STANDARD_PLANNER);
  const plannerData = plannerResource.data as HomeStandardPlannerData | null;
  // Memoized so the seven values keep a stable identity across renders. The
  // context values they replace were referentially stable between boot
  // publishes; fresh literals per render would re-run the Animals on Farm
  // snapshot below on every render, which is a change in cost, not in output.
  const {batches, breedingCycles, farrowingRecs, feederGroups, breeders, layerGroups, persistedMissedCleared} =
    React.useMemo(
      () => ({
        batches: (plannerData?.batches || []) as HomeRecord[],
        breedingCycles: (plannerData?.breedingCycles || []) as HomeRecord[],
        farrowingRecs: (plannerData?.farrowingRecs || []) as HomeRecord[],
        feederGroups: (plannerData?.feederGroups || []) as HomeRecord[],
        breeders: (plannerData?.breeders || []) as HomeRecord[],
        layerGroups: (plannerData?.layerGroups || []) as HomeRecord[],
        persistedMissedCleared: (plannerData?.missedCleared || []) as unknown[],
      }),
      [plannerData],
    );
  // The layer directory behind the Animals on Farm layer figure. Read through
  // animal.history.layerDirectory, which /animal-history already owns: the same
  // two tables, the same `select('*')`, and App's boot orderings preserved
  // (order('name') / order('start_date')), feeding the same
  // buildAnimalHistorySnapshot call. One resource, two declared surfaces —
  // cloning it would have duplicated twelve lines and added two physical call
  // sites for no difference in behaviour.
  //
  // Do NOT reintroduce useLayer() here, for the reason spelled out over the
  // planner above: a provider-hook field read is what
  // tests/static/former_eager_evidence_static.test.js scores as consumption,
  // and `home`'s retirement from the frozen layer_batches / layer_housings
  // consumer lists would become a false claim.
  const layerDirectoryResource = useRouteResource(RESOURCE.ANIMAL_HISTORY_LAYER_DIRECTORY);
  const layerDirectoryUnavailable = Boolean(layerDirectoryResource.error);
  const {layerBatches, layerHousings} = React.useMemo(() => {
    const directory = layerDirectoryResource.data as AnimalHistoryLayerDirectoryData | null;
    return {
      layerBatches: (directory?.layerBatches || []) as HomeRecord[],
      layerHousings: (directory?.layerHousings || []) as HomeRecord[],
    };
  }, [layerDirectoryResource.data]);
  // The COMPLETE broiler and pig daily histories — broilersOnFarmAt and
  // pigsOnFarmAt walk every report at or before today, so no bounded window
  // reproduces them. home.standard.animal.dailyHistory calls App's own boot
  // loaders (loadAllPoultryDailys / loadAllPigDailys), so the rows, the
  // deleted_at filter, both sort keys and the throw-after-a-failed-page fix are
  // the boot's, not a re-implementation — and no new physical call site appears
  // for either table. It is FAIL-CLOSED where the boot's `.catch(() => [])` was
  // not, because these arrays feed two NUMBERS on the card below; the two LISTS
  // that also read them degrade to the same empty array the boot already gave
  // them.
  const dailyHistoryResource = useRouteResource(RESOURCE.HOME_STANDARD_ANIMAL_DAILY_HISTORY);
  const dailyHistoryUnavailable = Boolean(dailyHistoryResource.error);
  const {broilerDailys, pigDailys} = React.useMemo(() => {
    const history = dailyHistoryResource.data as HomeStandardAnimalDailyHistoryData | null;
    return {
      broilerDailys: (history?.broilerDailys || []) as HomeRecord[],
      pigDailys: (history?.pigDailys || []) as HomeRecord[],
    };
  }, [dailyHistoryResource.data]);
  // Clear / Clear all used to publish through FeedCostsContext, which repainted
  // Home synchronously. A query result cannot, so the keys cleared during this
  // mount are held here and unioned over the persisted set — which is what the
  // context value did, including not rolling back a failed write. A remount
  // re-reads from the server, so the divergence cannot outlive the page.
  const [clearedThisMount, setClearedThisMount] = React.useState<Set<string>>(() => new Set());
  // Review F4: the missed card caps at 8 rows and expands on demand.
  const [missedShown, setMissedShown] = React.useState(8);
  const missedCleared = React.useMemo(
    () => new Set<any>([...persistedMissedCleared, ...clearedThisMount]),
    [persistedMissedCleared, clearedThisMount],
  );
  const {setView} = useUI();
  const navigate = useNavigate();

  // Coming-soon overlay target for not-yet-built top-level areas (Processing /
  // future sections). null = show the dashboard; a label string =
  // show the in-app coming-soon page. Cleared by its Back button.
  const [comingSoon, setComingSoon] = React.useState(null);
  const productionYear = new Date().getFullYear();
  const homeProductionParams = React.useMemo(() => ({year: productionYear}), [productionYear]);
  // The two windows App's boot sidecar used for these projections: 45 calendar
  // days for the layer/egg feeds, 14 for cattle/sheep. Pinned for the life of
  // the mount, which is exactly what the sidecar did — it resolved both cutoffs
  // once per cold boot and never re-ran. Putting them in the key rather than
  // deriving them inside the query is what stops a later mount on a new farm
  // day from reading yesterday's narrower window out of a retained entry.
  const homeAlertParams = React.useMemo(
    () => ({
      recentCutoff: toISO(addDays(todayCentralISO(), -45)),
      dailyCutoff: toISO(addDays(todayCentralISO(), -14)),
    }),
    [],
  );
  const alertInputsResource = useRouteResource(RESOURCE.HOME_STANDARD_ALERT_INPUTS, homeAlertParams);
  // An absent payload paints exactly what the boot sidecar painted before its
  // six independent requests landed: empty projections. None of them has ever
  // been allowed to gate or fail the dashboard, and that stays true here.
  //
  // Memoized so the six fallbacks keep a stable identity across renders. The
  // context values they replace were referentially stable between boot
  // publishes; six fresh `[]` literals per render would instead re-run the
  // Animals on Farm snapshot below on every render while the read is in
  // flight, which is a change in cost, not in output.
  const alertInputs = alertInputsResource.data as HomeStandardAlertInputsData | null;
  const {layerDailysRecent, eggDailysRecent, cattleDailysRecent, sheepDailysRecent, cattleForHome, sheepForHome} =
    React.useMemo(
      () => ({
        layerDailysRecent: (alertInputs?.layerDailysRecent || []) as HomeRecord[],
        eggDailysRecent: (alertInputs?.eggDailysRecent || []) as HomeRecord[],
        cattleDailysRecent: (alertInputs?.cattleDailysRecent || []) as HomeRecord[],
        sheepDailysRecent: (alertInputs?.sheepDailysRecent || []) as HomeRecord[],
        cattleForHome: (alertInputs?.cattleForHome || []) as HomeRecord[],
        sheepForHome: (alertInputs?.sheepForHome || []) as HomeRecord[],
      }),
      [alertInputs],
    );
  // The COMPLETE layer count evidence, and the one read on this page that is
  // FAIL-CLOSED alongside the planner. Every other optional read here feeds a
  // LIST, where an absent payload legibly shows fewer rows. This one feeds a
  // NUMBER, and an empty array does not make a number absent — it makes it 0,
  // because layersOnFarmAt falls back to `housing.current_count`, which is 0 on
  // every active layer housing on this farm. Painting 0 out of a transport
  // failure would report an empty flock as confidently as a real count, so the
  // card says "Unavailable" and offers Retry instead. No params: a complete
  // history has no farm-day identity to key on.
  const layerEvidenceResource = useRouteResource(RESOURCE.HOME_STANDARD_ANIMAL_LAYER_EVIDENCE);
  const layerEvidenceUnavailable = Boolean(layerEvidenceResource.error);
  // Memoized for the same reason the alert-input fallbacks are: a fresh `[]`
  // per render would re-run the Animals on Farm snapshot on every render while
  // the read is in flight.
  const layerEvidence = React.useMemo(
    () => ((layerEvidenceResource.data as HomeStandardLayerEvidenceData | null)?.layerEvidence || []) as HomeRecord[],
    [layerEvidenceResource.data],
  );
  const equipmentResource = useRouteResource(RESOURCE.HOME_STANDARD_EQUIPMENT_ATTENTION);
  const materialsResource = useRouteResource(RESOURCE.HOME_STANDARD_EQUIPMENT_MATERIALS);
  // Owner answer D (migration 221 lane): Production is hidden from Equipment
  // Tech, so its Home card -- the page's entry point -- is not rendered and its
  // sources are not read (list_production_legacy_events refuses the role).
  const showProductionCard = canAccessProduction(authState as AccessAuthState);
  const productionResource = useRouteResource(RESOURCE.HOME_STANDARD_PRODUCTION_SOURCES, homeProductionParams, {
    enabled: showProductionCard,
  });
  const clearMaterial = useRouteMutation<EquipmentMaterialClearCreateVars, null>(
    MUTATION.EQUIPMENT_MATERIAL_CLEAR_CREATE,
  );
  // Query correctly retains a same-key payload while an explicit retry is in
  // flight. The old page-local effects showed their loading state during that
  // retry, so preserve the card-level UX without reintroducing duplicate reads.
  const [equipmentRefreshing, setEquipmentRefreshing] = React.useState(false);
  const [materialsRefreshing, setMaterialsRefreshing] = React.useState(false);
  const [productionRefreshing, setProductionRefreshing] = React.useState(false);

  const showAllDailyReports = canSeeAllDailyReportsFeed(authState as AccessAuthState);

  // Owner answer E (2026-10-09): Equipment Tech holds Home, Equipment, Tasks
  // and Pasture — no livestock. The livestock sections below (Animals on Farm,
  // Missing Data, the missed-report panel with its Clear / Clear All writes,
  // and Next 30 Days) do not render for the tech, exactly as the Production
  // card above is already withheld by canAccessProduction. The reads behind
  // them are unchanged and fail closed for the role server-side; this decides
  // only whether the blocks render.
  const showLivestockSections = canSeeLivestockHomeSections(authState as AccessAuthState);

  // ── Greeting ──
  // Time-of-day word and the real calendar date, both resolved in
  // America/Chicago so they agree with the farm day boundary every other Home
  // surface uses (missed-report checks, Last 5 Days, daily uniqueness).
  //
  // This tracks the LIVE clock: Home stays mounted for hours on a farm office
  // screen, so a value frozen at mount would still say "Good morning" at 4pm and
  // would show yesterday's date after farm midnight. The effect re-arms itself
  // at the next boundary (noon / 6pm / farm midnight), clamped so a machine that
  // slept through one resyncs on wake. State is only replaced when the rendered
  // strings actually differ, so an ordinary tick costs no re-render.
  const [greeting, setGreeting] = React.useState(() => centralGreeting(new Date()));
  React.useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const next = centralGreeting(new Date());
      setGreeting((prev) => (prev.word === next.word && prev.dateText === next.dateText ? prev : next));
      timer = setTimeout(tick, msUntilNextGreetingBoundary(new Date()));
    };
    timer = setTimeout(tick, msUntilNextGreetingBoundary(new Date()));
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);
  const greetingWord = greeting.word;
  const greetingDate = greeting.dateText;
  // First name only; falls back to no name rather than an email or a guess.
  const greetingName = (authState?.name || '').trim().split(/\s+/)[0] || '';

  // Equipment data for the maintenance section below. A fetch failure
  // (permission/network) must surface as a visible error, never as a silently
  // empty/vanished section, so its scoped resource keeps loading + error state
  // and an explicit retry. attachment_checklists is included so the mig-048
  // Materials card can resolve attachment-based service groups without a
  // second query.
  const equipmentData = equipmentResource.error
    ? null
    : (equipmentResource.data as HomeStandardEquipmentAttentionData | null);
  const equipment = equipmentData?.equipment || [];
  const equipmentCompletions = equipmentData?.equipmentCompletions || {};
  const equipmentFuelings = equipmentData?.equipmentFuelings || {};
  const equipmentLoading = equipmentResource.loading || equipmentRefreshing;
  const equipmentError = equipmentResource.error;
  // mig 048 — the materials dashboard card below. Missing tables (mig 048 not
  // yet applied) is a distinct, non-error "feature not present" state from a
  // genuine query failure, which now gets its own loading/error tracking.
  const materialsData = materialsResource.error
    ? null
    : (materialsResource.data as HomeStandardEquipmentMaterialsData | null);
  const materials = materialsData?.materials || [];
  const materialClears = materialsData?.materialClears || [];
  const materialsTablesMissing = materialsData?.tablesMissing === true;
  const materialsLoading = materialsResource.loading || materialsRefreshing;
  const materialsError = materialsResource.error;
  const retryEquipment = React.useCallback(async () => {
    setEquipmentRefreshing(true);
    try {
      await equipmentResource.refetch();
    } finally {
      setEquipmentRefreshing(false);
    }
  }, [equipmentResource.refetch]);

  // mig 048 — Materials + clears for the home dashboard card. After the
  // 2026-05-14 retirement of the standalone /fleet/materials page, this
  // card is the only operator-facing surface. Its scoped refetch runs after a
  // successful Clear so the row vanishes from the
  // home view immediately.
  const retryMaterials = React.useCallback(async () => {
    setMaterialsRefreshing(true);
    try {
      await materialsResource.refetch();
    } finally {
      setMaterialsRefreshing(false);
    }
  }, [materialsResource.refetch]);

  // No refreshing flag alongside this one: the planner branch below is a
  // whole-page state, so `plannerResource.loading` is already what it renders.
  const retryPlanner = React.useCallback(() => {
    void plannerResource.refetch();
  }, [plannerResource.refetch]);

  const productionSources = productionResource.error
    ? null
    : (productionResource.data as HomeStandardProductionSourcesData | null);
  const productionLoading = productionResource.loading || productionRefreshing;
  const retryProduction = React.useCallback(async () => {
    setProductionRefreshing(true);
    try {
      await productionResource.refetch();
    } finally {
      setProductionRefreshing(false);
    }
  }, [productionResource.refetch]);

  // The Animals on Farm card is fed by THREE fail-closed reads, not one, so the
  // busy flag and the Retry are the card's rather than the layer figure's. Each
  // species figure below is still gated on its OWN reads — a poultry outage
  // must not blank the layer count — but one Retry re-fetches all three,
  // because an operator looking at a card with two "Unavailable" cells has one
  // intent, and refetching a resource that already resolved is a no-op the
  // query layer already de-duplicates.
  const [animalReadsRefreshing, setAnimalReadsRefreshing] = React.useState(false);
  const layerEvidenceLoading = layerEvidenceResource.loading || animalReadsRefreshing;
  const layerDirectoryLoading = layerDirectoryResource.loading || animalReadsRefreshing;
  const dailyHistoryLoading = dailyHistoryResource.loading || animalReadsRefreshing;
  // Layer Hens needs BOTH layer reads: the directory decides which housings
  // exist at all and the evidence decides what each one holds. Either one
  // absent makes the figure wrong rather than smaller.
  const layerCountUnavailable = layerEvidenceUnavailable || layerDirectoryUnavailable;
  const layerCountLoading = layerEvidenceLoading || layerDirectoryLoading;
  const animalsUnavailable = layerCountUnavailable || dailyHistoryUnavailable;
  const retryAnimalReads = React.useCallback(async () => {
    setAnimalReadsRefreshing(true);
    try {
      await Promise.all([
        layerEvidenceResource.refetch(),
        layerDirectoryResource.refetch(),
        dailyHistoryResource.refetch(),
      ]);
    } finally {
      setAnimalReadsRefreshing(false);
    }
  }, [layerEvidenceResource.refetch, layerDirectoryResource.refetch, dailyHistoryResource.refetch]);

  // Auto-status counts for poultry.
  // The Broilers launch tile was the only consumer of computeBroilerOnFarmCounts
  // here (its "N active · N on farm" subtitle). Home's live broiler count is now
  // the Animals on Farm card, which reads buildAnimalHistorySnapshot below; the
  // missed-report alerts use their own activeBroilerBatches2 set. Deriving an
  // on-farm figure Home no longer renders would be dead work on every render.
  const plannedBatches = batches.filter((b) => calcPoultryStatus(b) === 'planned');
  const processedBatches = batches.filter((b) => calcPoultryStatus(b) === 'processed');

  const weekEvents = buildNext30Events({batches, breedingCycles, farrowingRecs, feederGroups} as unknown as Parameters<
    typeof buildNext30Events
  >[0]);
  const animalSnapshot = React.useMemo(
    () =>
      buildAnimalHistorySnapshot(
        {
          batches,
          broilerDailys,
          layerBatches,
          layerHousings,
          layerDailys: layerEvidence,
          feederGroups,
          breeders,
          pigDailys,
          cattle: cattleForHome,
          sheep: sheepForHome,
        } as Parameters<typeof buildAnimalHistorySnapshot>[0],
        todayCentralISO(),
      ),
    [
      batches,
      broilerDailys,
      layerBatches,
      layerHousings,
      layerEvidence,
      feederGroups,
      breeders,
      pigDailys,
      cattleForHome,
      sheepForHome,
    ],
  );

  // Missed daily reports: checks last 7 days, persists until cleared.
  //
  // Migration 246 moved the persistence transport from the whole-set client
  // upsert in appStoreBlobApi to `app_store_apply_operation`
  // (`missed.clear_entries` / `missed.clear_all` in appStoreOperationApi),
  // which unions new entries onto the stored `ppp-missed-cleared-v1` array
  // under a per-key server advisory lock so concurrent clears cannot overwrite
  // each other. The ordering (optimistic repaint FIRST) and fire-and-forget
  // posture are unchanged: nothing here awaits and nothing here invalidates.
  async function clearMissedEntry(key: string) {
    setClearedThisMount((prev) => new Set([...prev, key]));
    clearMissedEntryOnServer(sb, key);
  }
  async function clearAllMissed(keys: string[]) {
    setClearedThisMount((prev) => new Set([...prev, ...keys]));
    clearAllMissedOnServer(sb, keys);
  }
  const allMissed = buildMissedDailyReports({
    batches,
    broilerDailys,
    pigDailys,
    layerDailysRecent,
    cattleDailysRecent,
    sheepDailysRecent,
    feederGroups,
    breeders,
    layerGroups,
    cattleForHome,
    sheepForHome,
    missedCleared,
  } as unknown as Parameters<typeof buildMissedDailyReports>[0]);
  // The list above reads the broiler/pig histories AND the recent layer,
  // cattle and sheep reports. Until all of them have loaded it would name every
  // active target as missed, so the section shows a loading state instead of a
  // count (Ronnie's note 6, 2026-10-06), and a failed read shows Unavailable
  // with Retry rather than a list of reports that may well exist (F04, F05).
  const [missedReadsRefreshing, setMissedReadsRefreshing] = React.useState(false);
  const missedReportsLoading = dailyHistoryLoading || alertInputsResource.loading || missedReadsRefreshing;
  // The alert inputs resolve each slice to [] on its own failure (the Animals
  // card and Last 5 Days stay fail-soft) and name the failed slices; the egg
  // feed is not a missed-report input.
  const missedInputSliceFailed = (alertInputs?.sliceErrors || []).some((slice) => slice !== 'eggDailysRecent');
  const missedReportsUnavailable =
    !missedReportsLoading && (dailyHistoryUnavailable || Boolean(alertInputsResource.error) || missedInputSliceFailed);
  const retryMissedReads = React.useCallback(async () => {
    setMissedReadsRefreshing(true);
    try {
      await Promise.all([dailyHistoryResource.refetch(), alertInputsResource.refetch()]);
    } finally {
      setMissedReadsRefreshing(false);
    }
  }, [dailyHistoryResource.refetch, alertInputsResource.refetch]);

  // Missing data: records missing a fact someone must enter (Ronnie's note 2).
  // Planner-only input, so it is ready whenever this page renders at all. Rows
  // open the batch record only for people whose access reaches it.
  const missingData = buildMissingData({batches, today: todayCentralISO()} as Parameters<typeof buildMissingData>[0]);
  const canOpenBroilerBatches = canAccessProgram(authStateValue as AuthState, 'broiler');

  // Equipment attention: overdue services + every-fillup item streaks + warranty.
  const equipmentAttention = buildEquipmentAttention({
    equipment,
    equipmentFuelings,
    equipmentCompletions,
    missedCleared,
  });

  // mig 048 — Materials Needed dashboard card.
  // After the 2026-05-14 retirement of the standalone /fleet/materials
  // page, this card is the only operator-facing materials surface. The
  // fuelingsBy map gets fed into buildMaterialChecklist alongside the
  // equipment list + materials + clears so the helper can derive next_due
  // via the existing computeIntervalStatus math. Per Codex (lane
  // amendment) Clear is one-material-at-a-time only — no bulk clear.
  const materialsFuelingsBy = React.useMemo(() => {
    const m = new Map();
    for (const eqId of Object.keys(equipmentFuelings || {})) {
      m.set(eqId, equipmentFuelings[eqId]);
    }
    return m;
  }, [equipmentFuelings]);
  const materialsChecklist = React.useMemo(() => {
    if (materialsTablesMissing) return [];
    if (!Array.isArray(equipment) || equipment.length === 0) return [];
    return buildMaterialChecklist({
      equipment,
      fuelingsBy: materialsFuelingsBy,
      materials,
      clears: materialClears,
    });
  }, [equipment, materialsFuelingsBy, materials, materialClears, materialsTablesMissing]);
  async function clearMaterialOne(material: MaterialRow, group: MaterialGroup) {
    const id = `emc-h-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const row = {
      id,
      material_id: material.id,
      equipment_id: material.equipment_id,
      due_bucket_value: group.due_bucket_value,
      due_bucket_unit: group.due_bucket_unit,
      cleared_at: new Date().toISOString(),
    };
    try {
      await clearMaterial.run({row});
    } catch (err) {
      // Treat unique-key collision as a no-op (already cleared in this bucket).
      //
      // The predicate is unchanged: `writeErrorMessage` renders
      // `String(err.message)`, and the catalog rethrows PostgREST's message
      // verbatim, so the same regex sees the same text it always did. The
      // SQLSTATE is now available too (`writeErrorCode(err) === '23505'`) and is
      // deliberately NOT substituted here — swapping the predicate would be a
      // behaviour change smuggled inside a conversion.
      if (!/duplicate key|23505/i.test(writeErrorMessage(err))) {
        console.error('clearMaterialOne:', err);
        return;
      }
    }
    // `retryMaterials()` STAYS, and the declared invalidation does not supersede
    // it. It is not a bare refetch: it brackets the call in
    // `setMaterialsRefreshing`, and that flag is the ONLY thing that renders the
    // card's busy state — `routeResourceResult` computes `loading` as
    // `isPending || (isFetching && data === undefined)`, which is false for a
    // refetch that already has data. Dropping it would silently delete a
    // visible spinner. The cost is one extra GET of a small card on a rare
    // click; the alternative is a UI change this lane may not make.
    //
    // It also runs on the tolerated-duplicate path, exactly as before, so the
    // cleared row still vanishes from the card when a concurrent clear won.
    await retryMaterials();
  }

  const activeBroilerBatches2 = batches.filter((b) => calcPoultryStatus(b) === 'active');
  // All-clear banner reflects ACTUAL pig daily targets: active feeder
  // sub-batches with pigs remaining or non-archived breeders. An active parent
  // feeder group with no active/live sub-batches is not a daily target, so it
  // no longer counts here.
  const hasAnyActivePig =
    activePigFeederDailyTargets(feederGroups, {breeders}).length > 0 || (breeders || []).some((b) => !b.archived);
  // The all-clear banner's layer term. Same owner and same rule as the missed
  // report list directly above it: only an ACTIVE canonical Layer Group is a
  // daily target, with legacy/unknown statuses and disagreeing duplicates both
  // resolving to inactive. Keeping the two on one helper is what stops the
  // banner from claiming "all active batches reported" for a program the list
  // has already stopped tracking, or vice versa.
  const activeLayerGroups2 = filterActiveLayerGroups(layerGroups);
  const productionModel = React.useMemo(
    () => buildProductionModel((productionSources || {}) as Parameters<typeof buildProductionModel>[0]),
    [productionSources],
  );
  const productionSourceErrors = (productionSources && productionSources.sourceErrors) || {};
  const hasProductionErrors = Object.keys(productionSourceErrors).length > 0;
  const productionStats = React.useMemo(
    () => homeProductionStats(productionModel, productionYear, productionSourceErrors),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- productionSourceErrors is a plain object recomputed each render from productionSources; keying on productionSources itself is the stable dependency.
    [productionModel, productionYear, productionSources],
  );

  // ── Admin weekly table data ──
  const fiveDaysAgo = toISO(addDays(todayCentralISO(), -5));
  const weekAgo = fiveDaysAgo; // used for admin daily tiles (5 days)
  // Stable kind keys (broiler/pig/layer/egg/cattle/sheep) drive sort order
  // and styling; emoji + label stay separate from the kind so the display
  // layer can swap to PNG icons without touching the data shape. Codex
  // amendment: "🐔 Broiler"-string-keyed lookups were refactored away.
  const allRecentReports = [
    ...broilerDailys
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'broilerdailys', date: d.date, kind: 'broiler', label: 'Broiler', raw: d})),
    ...pigDailys
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'pigdailys', date: d.date, kind: 'pig', label: 'Pig', raw: d})),
    ...layerDailysRecent
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'layerdailys', date: d.date, kind: 'layer', label: 'Layer', raw: d})),
    ...eggDailysRecent
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'eggdailys', date: d.date, kind: 'egg', label: 'Egg', raw: d})),
    ...cattleDailysRecent
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'cattledailys', date: d.date, kind: 'cattle', label: 'Cattle', raw: d})),
    ...sheepDailysRecent
      .filter((d) => d.date >= weekAgo)
      .map((d) => ({id: d.id, view: 'sheepdailys', date: d.date, kind: 'sheep', label: 'Sheep', raw: d})),
  ].sort((a, b) => b.date.localeCompare(a.date) || a.kind.localeCompare(b.kind));

  // (The active-cycle / total-sow tallies that fed the Pigs launch tile's
  // subtitle went with that tile; the pig program dashboard owns those figures.)

  // Performance trends
  // Pig farrowing survival per cycle (most recent 5)
  const _homeSeqMap = buildCycleSeqMap(breedingCycles);
  const cycleSurvival = breedingCycles
    .map((c) => {
      const tl = calcBreedingTimeline(c.exposureStart);
      if (!tl) return null;
      const recs = farrowingRecs.filter((r) => {
        if (r.group !== c.group || !r.farrowingDate) return false;
        const rd = new Date(r.farrowingDate + 'T12:00:00');
        return rd >= new Date(tl.farrowingStart + 'T12:00:00') && rd <= addDays(tl.farrowingEnd, 14);
      });
      if (recs.length === 0) return null;
      const born = recs.reduce((s, r) => s + (parseInt(r.totalBorn) || 0), 0);
      const dead = recs.reduce((s, r) => s + (parseInt(r.deaths) || 0), 0);
      const _suf = _homeSeqMap[c.id];
      return {
        label: `G${c.group}${_suf ? ' · ' + _suf : ''} ${fmtS(c.exposureStart)}`,
        survival: born > 0 ? Math.round(((born - dead) / born) * 100) : 0,
        recs: recs.length,
      };
    })
    .filter(Boolean)
    .slice(-5);

  // Pig carcass yield trend
  const yieldData = feederGroups
    .flatMap((g) =>
      (g.processingTrips || []).map((t: HomeRecord) => {
        const live = (t.liveWeights || '')
          .split(/[\s,]+/)
          .map((v: string) => parseFloat(v))
          .filter((v: number) => !isNaN(v) && v > 0)
          .reduce((a: number, b: number) => a + b, 0);
        const hang = parseFloat(t.hangingWeight) || 0;
        return live > 0 && hang > 0
          ? {label: t.date, yld: Math.round((hang / live) * 1000) / 10, batch: g.batchName}
          : null;
      }),
    )
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label))
    .slice(-8);

  const statCard = (label: string, val: React.ReactNode, color = '#187A54', sub: React.ReactNode = '') => (
    <div
      key={label}
      style={{
        background: 'white',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '14px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: '#68707A',
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{fontSize: 26, fontWeight: 700, color, lineHeight: 1}}>{val}</div>
      {sub && <div style={{fontSize: 11, color: '#68707A', marginTop: 3}}>{sub}</div>}
    </div>
  );

  if (comingSoon) {
    return <ComingSoonPage Header={Header} label={comingSoon} onBack={() => setComingSoon(null)} />;
  }

  // FAIL-CLOSED on the planner, and only on the planner.
  //
  // Every other read on this page is optional and already renders its own
  // truthful absent state. This one is not: the seven `ppp-*` blobs decide the
  // Animals on Farm counts, the missed-report list, the all-clear banner and
  // Next 30 Days. The planner resource rejects a failed app_store read.
  // The dashboard shows an explicit unavailable state rather than an empty
  // planner. Painting empty arrays here would report an empty farm out of
  // a transport failure, which is the same lie home.light.planner and
  // layer.batches.core both refuse.
  if (plannerResource.loading || (!plannerData && !plannerResource.error)) {
    return (
      <div className="home theme-crisp home-dashboard">
        <Header />
        <main className="home-col">
          <section className="card empty-note" data-home-planner-loading="1" aria-live="polite">
            Loading your farm data…
          </section>
        </main>
      </div>
    );
  }
  if (plannerResource.error || !plannerData) {
    return (
      <div className="home theme-crisp home-dashboard">
        <Header />
        <main className="home-col">
          <section className="card empty-note" data-home-planner-error="1" role="alert">
            Your farm planner data is unavailable right now.
            <Button variant="secondary" data-home-planner-retry="1" onClick={retryPlanner}>
              Retry
            </Button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="home theme-crisp home-dashboard">
      <Header />
      <main className="home-col">
        {/* ── Greeting ── the page opens on who/when, then goes straight to real
            farm numbers. The ten launch tiles that used to sit here (six
            programs + Pasture Map + Weather + Processing + Admin) are retired:
            every one of those destinations now lives in the persistent global
            header, reachable from anywhere instead of only from Home, and the
            header applies the same program_access / role gates the tiles did.
            Nothing here is inferred — the date is the farm's real Central
            calendar date, and no sync/freshness/"all current" claim is made
            because no truthful runtime signal backs one. */}
        <section className="home-greeting" data-home-greeting="1">
          <h1 className="home-greeting-title">
            {greetingWord}
            {greetingName ? `, ${greetingName}` : ''}
          </h1>
          <p className="home-greeting-date" data-home-greeting-date="1">
            {greetingDate}
          </p>
        </section>

        {/* ── Animals on Farm ── */}
        {showLivestockSections &&
          (() => {
            return (
              // Real on-farm counts (kept). The whole card opens the month-by-month
              // Animals on Farm history page. data-home-grid="animals" stays on the
              // row; species use the design dot+label treatment.
              //
              // The Retry control is a PRECEDING SIBLING of the card button, never
              // a child of it — the same invalid-interactive-nesting rule the
              // Production card below is built around: a keydown on a nested
              // button bubbles to the ancestor's own Enter/Space handler and fires
              // both actions at once. The card itself is unchanged markup, which
              // is also why `button.card.stats` still identifies it.
              <div data-home-animals-card="1">
                {!animalReadsRefreshing && animalsUnavailable && (
                  <div className="production-card-retry-row" data-home-animals-error="1" role="alert">
                    <Button variant="secondary" data-home-animals-retry="1" onClick={retryAnimalReads}>
                      Retry
                    </Button>
                  </div>
                )}
                <button type="button" className="card stats lift" onClick={() => setView('animalHistory')}>
                  <div className="stats-head">
                    <div className="card-label">Animals on Farm</div>
                    <Chevron className="go" />
                  </div>
                  <div className="stat-row" data-home-grid="animals">
                    {/* Gated on home.standard.animal.dailyHistory for the same
                      reason the layer figure is gated on its own reads: an
                      absent poultry history does not make this number absent,
                      it makes it WRONG. broilersOnFarmAt subtracts mortality it
                      reads out of those rows, so an empty array paints 2269
                      against a live 2172 — an outage reported as 97 birds that
                      are not on the farm. */}
                    <div className="stat">
                      <div
                        className={
                          'stat-n' + (!dailyHistoryLoading && dailyHistoryUnavailable ? ' stat-n-unavailable' : '')
                        }
                        data-home-animals-broiler-state={
                          dailyHistoryLoading ? 'loading' : dailyHistoryUnavailable ? 'unavailable' : 'ready'
                        }
                      >
                        {dailyHistoryLoading ? (
                          '--'
                        ) : dailyHistoryUnavailable ? (
                          'Unavailable'
                        ) : animalSnapshot.broilersEstimated ? (
                          // A batch with no day-1 count yet is counted at its
                          // ordered birds: gray value + 'est.' (D06, Q50).
                          <span className="stat-estimated" data-home-animals-broiler-estimated="1">
                            {animalSnapshot.broilers.toLocaleString()}
                            <span className="stat-est">est.</span>
                          </span>
                        ) : (
                          animalSnapshot.broilers.toLocaleString()
                        )}
                      </div>
                      <div className="stat-l">
                        <span className="sdot sdot-broiler" />
                        Broilers
                      </div>
                    </div>
                    {/* Gated on BOTH layer reads. An absent layer_dailys payload
                      does not make this number absent, it makes it 0 —
                      layersOnFarmAt falls back to `housing.current_count`,
                      which is 0 on every active housing here. An absent
                      layer_housings payload is worse: with no housings every
                      batch falls to the housing-less branch and contributes its
                      undepleted original_count, which paints 1000 against a
                      live 564. "Unavailable" is the only truthful paint for
                      either failure. Cattle and Sheep are NOT gated: they come
                      from home.standard.alert.inputs, which is fail-soft
                      because its other five legs feed LISTS — that split is
                      declared in the resource and is left exactly as it was. */}
                    <div className="stat">
                      <div
                        className={
                          'stat-n' + (!layerCountLoading && layerCountUnavailable ? ' stat-n-unavailable' : '')
                        }
                        data-home-animals-layer-state={
                          layerCountLoading ? 'loading' : layerCountUnavailable ? 'unavailable' : 'ready'
                        }
                      >
                        {layerCountLoading
                          ? '--'
                          : layerCountUnavailable
                            ? 'Unavailable'
                            : animalSnapshot.layers.toLocaleString()}
                      </div>
                      <div className="stat-l">
                        <span className="sdot sdot-layer" />
                        Layer Hens
                      </div>
                    </div>
                    {/* Gated on the same read as Broilers. pigsOnFarmAt takes the
                      newest positive pig_count per feeder target and nets out
                      later trips, mortalities and transfers; with no dailys
                      every target falls back to started-minus-events. That
                      happens to equal the live 63 today only because no active
                      cohort has diverged from its starting count — a data
                      coincidence, not a property, and not a thing to paint a
                      number on. */}
                    <div className="stat">
                      <div
                        className={
                          'stat-n' + (!dailyHistoryLoading && dailyHistoryUnavailable ? ' stat-n-unavailable' : '')
                        }
                        data-home-animals-pig-state={
                          dailyHistoryLoading ? 'loading' : dailyHistoryUnavailable ? 'unavailable' : 'ready'
                        }
                      >
                        {dailyHistoryLoading
                          ? '--'
                          : dailyHistoryUnavailable
                            ? 'Unavailable'
                            : animalSnapshot.pigs.toLocaleString()}
                      </div>
                      <div className="stat-l">
                        <span className="sdot sdot-pig" />
                        Pigs
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-n">{animalSnapshot.cattle.toLocaleString()}</div>
                      <div className="stat-l">
                        <span className="sdot sdot-cattle" />
                        Cattle
                      </div>
                    </div>
                    <div className="stat">
                      <div className="stat-n">{animalSnapshot.sheep.toLocaleString()}</div>
                      <div className="stat-l">
                        <span className="sdot sdot-sheep" />
                        Sheep
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            );
          })()}

        {/* Retry is an independent control, deliberately NOT nested inside the
            "open Production" button below: a button-in-button (or a button
            inside a role="button" element) is invalid interactive nesting —
            besides the accessibility problem, a keydown on the nested Retry
            button would bubble to an ancestor's own Enter/Space handler and
            fire both actions at once. The two controls are siblings instead. */}
        {showProductionCard && (
          <div className="card stats lift" data-home-production-card="1">
            {!productionLoading && hasProductionErrors && (
              <div className="production-card-retry-row">
                <Button variant="secondary" data-home-production-retry="1" onClick={retryProduction}>
                  Retry
                </Button>
              </div>
            )}
            <button
              type="button"
              className="production-card-open"
              onClick={() => setView('production')}
              aria-label={`Open Production ${productionYear}`}
            >
              <div className="stats-head">
                <div className="card-label">Production - {productionYear}</div>
                <Chevron className="go" />
              </div>
              <div className="stat-row" data-home-grid="production">
                {productionStats.map((stat) => (
                  <div className="stat" key={stat.programKey} data-production-stat={stat.programKey}>
                    <div
                      className={'stat-n' + (!productionLoading && stat.unavailable ? ' stat-n-unavailable' : '')}
                      data-production-stat-state={
                        productionLoading ? 'loading' : stat.unavailable ? 'unavailable' : 'ready'
                      }
                    >
                      {productionLoading ? '--' : stat.unavailable ? 'Unavailable' : stat.value}
                    </div>
                    <div className="stat-l">
                      <span className={`sdot sdot-${stat.programKey === 'egg' ? 'layer' : stat.programKey}`} />
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </button>
          </div>
        )}

        {/* ── Missing Data ── records missing a fact someone must enter
              (Ronnie's note 2). Same audience as the missed-report list, its own
              rules: no Clear, a row leaves when the data is entered. Dashboard
              section keeps its title with one gray line when empty (F06);
              warn tone for missing data (Q21, Q13). */}
        {showLivestockSections && (
          <section className="block" data-home-missing-data="1">
            <div className="block-head">
              <h2 className="section-label">Missing Data</h2>
              {missingData.length > 0 && <Badge variant="warn">{missingData.length}</Badge>}
            </div>
            {missingData.length === 0 ? (
              <div className="card empty-note" data-home-missing-data-state="empty">
                Nothing missing
              </div>
            ) : (
              <ul className="panel">
                {missingData.map((m) => (
                  <li
                    key={m.key}
                    className={'litem' + (canOpenBroilerBatches ? ' is-link' : '')}
                    data-home-missing-data-row={m.key}
                    {...(canOpenBroilerBatches
                      ? openableProps(() => navigate('/broiler/batches/' + encodeURIComponent(m.batchName)))
                      : {})}
                  >
                    <span className="coin coin-sm">
                      <PlannerIcon iconKey={m.iconKey} size={22} />
                    </span>
                    <div className="litem-body">
                      <div className="litem-title">{m.batchName}</div>
                      <div className="litem-meta">{m.detail}</div>
                    </div>
                    {canOpenBroilerBatches && <Chevron className="row-go" />}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* ── Missed Daily Reports ── grouped panel; per-row + Clear-all
              actions preserved (clearMissedEntry / clearAllMissed). No count
              until every daily read has loaded (note 6): the title stays with
              a loading line, and a failed read says so with Retry. Livestock
              reports and their Clear writes do not render for Equipment Tech
              (owner answer E), so no tech Clear can reach the shared
              ppp-missed-cleared-v1 blob. */}
        {showLivestockSections && missedReportsLoading && (
          <section className="block" data-home-missed-dailys-state="loading">
            <div className="block-head">
              <h2 className="section-label">Missed Daily Reports</h2>
            </div>
            <div className="card empty-note" aria-live="polite">
              Loading daily reports…
            </div>
          </section>
        )}
        {showLivestockSections && missedReportsUnavailable && (
          <section className="block" data-home-missed-dailys-state="unavailable">
            <div className="block-head">
              <h2 className="section-label">Missed Daily Reports</h2>
            </div>
            <div className="card empty-note" role="alert">
              Daily reports could not be loaded, so missed reports can't be checked right now.
              <Button variant="secondary" onClick={retryMissedReads}>
                Retry
              </Button>
            </div>
          </section>
        )}
        {showLivestockSections && !missedReportsLoading && !missedReportsUnavailable && allMissed.length > 0 && (
          <section className="block">
            <div className="block-head">
              <h2 className="section-label label-danger">Missed Daily Reports</h2>
              <Badge variant="danger">{allMissed.length}</Badge>
              <Button
                variant="secondary"
                className="ml-auto"
                onClick={() => clearAllMissed(allMissed.map((m) => m.key))}
              >
                Clear All
              </Button>
            </div>
            <ul className="panel">
              {allMissed.slice(0, missedShown).map((m) => (
                <li key={m.key} className="litem">
                  <span className="coin coin-sm">
                    <PlannerIcon iconKey={m.iconKey} size={22} />
                  </span>
                  <div className="litem-body">
                    <div className="litem-title">{m.label}</div>
                    <div className="litem-meta">
                      {m.type} · No daily report for {fmt(m.date)}
                    </div>
                  </div>
                  <Button variant="secondary" onClick={() => clearMissedEntry(m.key)}>
                    Clear
                  </Button>
                </li>
              ))}
            </ul>
            {/* Review F4: one card cannot own the page — the card shows the
                newest 8 rows and expands on demand. The count badge, the
                per-row Clear on every shown row and Clear All (which still
                clears ALL rows, shown or not) are unchanged. */}
            {allMissed.length > missedShown && (
              <div style={{marginTop: 8}}>
                <Button variant="secondary" onClick={() => setMissedShown((n) => n + 8)} data-home-missed-show-more="1">
                  Show 8 More ({allMissed.length - missedShown} Hidden)
                </Button>
              </div>
            )}
          </section>
        )}
        {showLivestockSections &&
          !missedReportsLoading &&
          !missedReportsUnavailable &&
          allMissed.length === 0 &&
          (activeBroilerBatches2.length > 0 || hasAnyActivePig || activeLayerGroups2.length > 0) && (
            <div className="note note-ok">
              <span className="note-ic">
                <CheckGlyph />
              </span>
              All active batches had daily reports entered for the past 7 days
            </div>
          )}

        {/* ── Equipment Maintenance ── overdue intervals + every-fillup streaks
              + warranty. Renders immediately after the daily-report banner —
              the old collapsed weather card that used to sit here is gone;
              the header weather button now owns navigation to the dedicated
              /weather page. Always exactly one truthful state: loading,
              populated attention list, caught up/empty, or a visible
              unavailable error with retry — a query failure must never make
              this section silently disappear. Rows are NOT manually
              clearable except a warranty-only notice; each attention row
              auto-clears when its underlying state resolves (interval ticked
              complete on a fueling, every-fillup items ticked on next
              fueling, or warranty_expiration updated). */}
        <section className="block" data-home-equipment-maintenance="1">
          <div className="block-head">
            <span className="head-ic">
              <WrenchGlyph />
            </span>
            <h2 className="section-label">Equipment Maintenance</h2>
            {/* Q13: the count takes the tone of the worst item (L4). */}
            {!equipmentLoading && !equipmentError && equipmentAttention.length > 0 && (
              <Badge variant={equipmentAttention.some((a) => a.kind === 'overdue') ? 'danger' : 'warn'}>
                {equipmentAttention.length}
              </Badge>
            )}
          </div>
          {equipmentLoading ? (
            <div className="card empty-note" data-home-equipment-maintenance-state="loading">
              Loading equipment maintenance…
            </div>
          ) : equipmentError ? (
            <div className="card empty-note" data-home-equipment-maintenance-state="error">
              Equipment maintenance is unavailable right now.
              <Button variant="secondary" data-home-equipment-maintenance-retry="1" onClick={retryEquipment}>
                Retry
              </Button>
            </div>
          ) : equipmentAttention.length > 0 ? (
            <ul className="panel" data-home-equipment-maintenance-state="populated">
              {equipmentAttention.map((a) => {
                const led = a.kind === 'overdue' ? 'led-danger' : 'led-warn';
                const badge: 'danger' | 'warn' = a.kind === 'overdue' ? 'danger' : 'warn';
                // Quantity badge ("10 h overdue" / "6 skipped"): the shared Badge (Q16).
                const badgeText = a.pill || (a.kind === 'overdue' ? 'Overdue' : 'Skipped');
                // Warranty-only notices keep the manual Clear; any piece with a
                // service/checklist item is auto-clearing and shows the pill.
                const clearableKey = a.clearableKey;
                const isClearable = !!clearableKey;
                // Group of typed items for this one piece of equipment. Each
                // carries a type chip (Service / Checklist / Warranty) so a
                // 50-hour checklist streak never reads like a duplicate service
                // alert. The notice routes via its primary kind.
                const items: HomeRecord[] = Array.isArray(a.items) ? a.items : [];
                return (
                  <li
                    key={a.key}
                    className="litem eq is-link"
                    data-attention-kind={a.kind}
                    data-equipment-slug={a.slug}
                    {...openableProps(() =>
                      navigate(a.kind === 'fillup_streak' ? '/equipment/' + a.slug : '/fleet/' + a.slug),
                    )}
                  >
                    <span className={'eq-led ' + led} />
                    <div className="litem-body">
                      <div className="litem-title">{a.label}</div>
                      <div className="litem-types">
                        {items.map((it) => (
                          <div className="litem-type-row" key={it.key} data-attention-item-kind={it.kind}>
                            <span className={'type-chip type-' + it.type}>{it.typeLabel}</span>
                            {/* service: service-only meta (quantity is in the pill);
                                checklist/warranty fall back to the full detail. */}
                            <span className="litem-meta">{it.metaLabel || it.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {isClearable ? (
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearMissedEntry(clearableKey);
                        }}
                      >
                        Clear
                      </Button>
                    ) : (
                      <Badge variant={badge}>{badgeText}</Badge>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div
              className="note note-ok"
              data-home-equipment-maintenance-caught-up="1"
              data-home-equipment-maintenance-state="empty"
            >
              <span className="note-ic">
                <CheckGlyph />
              </span>
              Equipment maintenance is caught up
            </div>
          )}
        </section>

        {/* ── Equipment Materials (mig 048) ── compact rolling-checklist for
              every logged-in role (not admin-gated). Cleared rows vanish
              from the active list (Codex lane amendment 2). One-material-
              at-a-time clear only — no bulk Clear All. This is the
              canonical operator surface for materials after the standalone
              /fleet/materials page was retired 2026-05-14. Same one-of-four-
              states contract as Equipment Maintenance above. */}
        <section className="block" data-home-equipment-materials="1">
          <div className="block-head">
            <h2 className="section-label">Equipment Materials</h2>
          </div>
          {materialsLoading ? (
            <div className="card empty-note" data-home-equipment-materials-state="loading">
              Loading equipment materials…
            </div>
          ) : materialsError ? (
            <div className="card empty-note" data-home-equipment-materials-state="error">
              Equipment materials are unavailable right now.
              <Button variant="secondary" data-home-equipment-materials-retry="1" onClick={retryMaterials}>
                Retry
              </Button>
            </div>
          ) : materialsChecklist.length > 0 ? (
            <div className="panel" data-home-materials-card="1" data-home-equipment-materials-state="populated">
              {materialsChecklist.map((row) => (
                <div key={row.equipment.id} className="mat-eq" data-home-material-equipment={row.equipment.slug}>
                  <div className="mat-eq-name">{row.equipment.name}</div>
                  {row.groups.map((g) => {
                    const labelUnit = g.interval_unit === 'km' ? 'km' : g.interval_unit === 'use' ? '' : 'h';
                    const groupLabel =
                      g.interval_unit === 'use'
                        ? g.attachment_name
                          ? `${g.attachment_name} — Every Use`
                          : 'Every Use'
                        : g.attachment_name
                          ? `${g.attachment_name} — ${g.interval_value}${labelUnit}`
                          : `Every ${g.interval_value}${labelUnit}`;
                    const isOverdue = g.status?.overdue;
                    const dueLabel =
                      g.interval_unit === 'use'
                        ? 'always'
                        : isOverdue
                          ? 'OVERDUE'
                          : `due in ${g.status?.until_due ?? '—'}${labelUnit}`;
                    return (
                      <div key={g.groupKey} className={'mat-group' + (isOverdue ? ' is-overdue' : '')}>
                        <div className="mat-group-head">
                          <span className="mat-group-label">{groupLabel}</span>
                          <span className={'mat-group-due' + (isOverdue ? ' is-overdue' : '')}>{dueLabel}</span>
                        </div>
                        {g.materials.map((m) => (
                          <div key={m.id} className="mat-row" data-home-material-row={m.id}>
                            <span className="mat-name">{String(m.material_name || '')}</span>
                            {m.qty != null && String(m.qty) !== '' && (
                              <span className="mat-qty">
                                {String(m.qty)}
                                {m.unit ? ` ${String(m.unit)}` : ''}
                              </span>
                            )}
                            <Button
                              variant="secondary"
                              onClick={() => clearMaterialOne(m, g)}
                              data-home-material-clear={m.id}
                            >
                              Clear
                            </Button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : (
            <div className="note note-ok" data-home-materials-caught-up="1" data-home-equipment-materials-state="empty">
              <span className="note-ic">
                <CheckGlyph />
              </span>
              Equipment materials are caught up
            </div>
          )}
        </section>

        {/* ── Admin Weekly Report Table ── */}

        {/* What's happening this week */}
        {showLivestockSections && (
          <section className="block">
            <div className="block-head">
              <h2 className="section-label">Next 30 Days</h2>
            </div>
            {weekEvents.length === 0 ? (
              <div className="card empty-note">Nothing scheduled in the next 30 days</div>
            ) : (
              <ul className="panel">
                {weekEvents.map((e, i) => (
                  <li
                    key={i}
                    className={'litem fd' + (e.reminder ? ' is-link' : '')}
                    {...(e.reminder
                      ? openableProps(() => {
                          if (e.type === 'wt-4wk' || e.type === 'wt-6wk') {
                            setView('list');
                          }
                        })
                      : {})}
                  >
                    <span className="coin coin-sm">
                      <PlannerIcon iconKey={e.iconKey} size={18} />
                    </span>
                    <div className="litem-body">
                      <div className="litem-title">{e.label}</div>
                      <div className="litem-meta">
                        {e.subline || fmt(e.date)}
                        {e.reminder ? ' · click to open batch' : ''}
                      </div>
                    </div>
                    {/* Q10 retired the "Reminder" badge; the meta line already says "click to open batch". */}
                    {!e.reminder && <span className="dot" style={{background: e.color}} />}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {showAllDailyReports && allRecentReports.length > 0 && (
          <section className="block admin-daily-block">
            <div className="block-head">
              <h2 className="section-label">Last 5 Days — All Daily Reports</h2>
            </div>
            <div className="card admin-daily">
              {(() => {
                // Group by date, then within each date group by animal kind.
                // Lookups (order/colors/bg) key off the stable `kind` string,
                // not an emoji-prefixed display label, so the heading icon
                // can swap to PNG without churning the lookup map.
                const dates = [...new Set(allRecentReports.map((r) => r.date))].sort().reverse();
                const kindOrder: Record<string, number> = {broiler: 0, pig: 1, layer: 2, egg: 3, cattle: 4, sheep: 5};
                const kindColors: Record<string, string> = {
                  broiler: '#a16207',
                  pig: '#1e3a8a',
                  layer: '#92400e',
                  egg: '#78350f',
                  cattle: '#991b1b',
                  sheep: '#0f766e',
                };
                const kindIconKey: Record<string, string> = {
                  broiler: ANIMAL_ICON_KEYS.broiler,
                  pig: ANIMAL_ICON_KEYS.pig,
                  layer: ANIMAL_ICON_KEYS.layer,
                  egg: ANIMAL_ICON_KEYS.egg,
                  cattle: ANIMAL_ICON_KEYS.cattle,
                  sheep: ANIMAL_ICON_KEYS.sheep,
                };
                return dates.map((date) => {
                  const dayRecs = allRecentReports
                    .filter((r) => r.date === date)
                    .sort((a, b) => (kindOrder[a.kind] ?? 9) - (kindOrder[b.kind] ?? 9));
                  const kinds = [...new Set(dayRecs.map((r) => r.kind))];
                  return (
                    <div key={date} className="day-group">
                      <div className="day-head">
                        <span>{fmt(date)}</span>
                        <span className="day-count">
                          {dayRecs.length} report{dayRecs.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {kinds.map((kind) => {
                        const typeRecs = dayRecs.filter((r) => r.kind === kind);
                        const color = kindColors[kind] || '#374151';
                        const heading = (typeRecs[0] && typeRecs[0].label) || kind;
                        return (
                          <div key={kind} className="kind-group">
                            <div className="kind-head">
                              <PlannerIcon iconKey={kindIconKey[kind]} size={18} />
                              <span className="kind-dot" style={{background: color}} aria-hidden="true" />
                              <span className="kind-name">{heading.toUpperCase()}</span>
                            </div>
                            <div className="drc-stack">
                              {typeRecs.map((r) => {
                                const d = r.raw || {};
                                const open = () => {
                                  const path = pathForDailyReport(r);
                                  if (path) navigate(path);
                                };
                                const attrs = {'data-daily-report-tile': r.id};
                                if (kind === 'egg') {
                                  const total =
                                    (parseInt(d.group1_count) || 0) +
                                    (parseInt(d.group2_count) || 0) +
                                    (parseInt(d.group3_count) || 0) +
                                    (parseInt(d.group4_count) || 0);
                                  const breakdown = [
                                    [d.group1_name, d.group1_count],
                                    [d.group2_name, d.group2_count],
                                    [d.group3_name, d.group3_count],
                                    [d.group4_name, d.group4_count],
                                  ]
                                    .filter(([n, c]) => n && parseInt(c) > 0)
                                    .map(([n, c]) => ({loc: n, n: c}));
                                  return (
                                    <EggSummaryCard
                                      key={r.id}
                                      total={total}
                                      team={d.team_member || '—'}
                                      breakdown={breakdown}
                                      dozens={parseFloat(d.dozens_on_hand) > 0 ? d.dozens_on_hand + ' doz' : null}
                                      comment={commentText(d.comments)}
                                      onOpen={open}
                                      attrs={attrs}
                                    />
                                  );
                                }
                                return (
                                  <DailyRecordCard
                                    key={r.id}
                                    program={kind}
                                    {...buildHomeDailyModel(kind, d)}
                                    onOpen={open}
                                    attrs={attrs}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                });
              })()}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
